import { Component, OnInit } from '@angular/core';
import { Opcoes, Questionario, QuestionarioRespondido } from '../../core/interface/questionario';
import { CommonModule } from '@angular/common';
import { HttpClientService } from '../../shared/services/http-client.service';
import { PerfilParticipante } from '../../core/interface/perfil-participante';
import { AppStateService } from '../../shared/services/app-state.service';
import { routes } from '../../app.routes';
import { Router } from '@angular/router';
import { EnumStatusResponse } from '../../core/enum/EnumHttpResponse';
import { CampanhaPerfil } from '../../core/interface/campanha';
import { LoaderService } from '../../shared/services/loader.service';
import { ToastService } from '../../shared/services/toast.service';

@Component({
  selector: 'app-choice-history',
  imports: [CommonModule],
  templateUrl: './choice-history.component.html',
  styleUrl: './choice-history.component.scss',
})
export class ChoiceHistoryComponent implements OnInit {
  perfilParticipante?: PerfilParticipante;
  campanha!: CampanhaPerfil;
  questionarioRespondido!: QuestionarioRespondido[];
  token!: string;
  // Data/hora de finalização do questionário (menor dtInclusao das respostas).
  dataFinalizacao?: string;
  questionnaire!: Questionario[];
  // Perguntas indexadas por idPergunta, para localizar a ordem em O(1).
  private perguntasPorId = new Map<number, Questionario>();

  constructor(
    private httpClientService: HttpClientService,
    private appStateService: AppStateService,
    private router: Router,
    private loaderService: LoaderService,
    private toastService: ToastService
  ) { }

  ngOnInit(): void {
    const token = this.appStateService.getToken() ?? '';
    const campanha = this.appStateService.getCampanha();
    const perfilParticipante = this.appStateService.getPerfilParticipante();
    const questionarioEscolhido = this.appStateService.getEscolhaParticipante();

    if (!campanha || !perfilParticipante || !questionarioEscolhido || !token) {
      console.warn('Home carregado sem estado completo:', { campanha, perfilParticipante, questionarioEscolhido, token });
      this.router.navigate(['/not-found']);
      return;
    }

    this.perfilParticipante = perfilParticipante;
    this.campanha = campanha;
    this.token = token;
    this.getQuestionnaire(token, this.campanha.idQuestionario);
    this.getQuestionnaireParticipante(token, perfilParticipante.id);
  }

  goBack() {
    this.router.navigate(['/term']);
  }

  /**
   * Monta o título da pergunta no formato "{ordem}. {textoPergunta}".
   * A ordem vem de getQuestionnaire(), relacionada por idPergunta.
   * Se a pergunta não for encontrada, exibe apenas o textoPergunta.
   */
  getTituloPergunta(resposta: QuestionarioRespondido): string {
    const pergunta = this.perguntasPorId.get(resposta.idPergunta);

    return pergunta
      ? `${pergunta.ordem}. ${resposta.textoPergunta}`
      : resposta.textoPergunta;
  }

  private getQuestionnaireParticipante(token: string, idParticipante: number): void {
    this.loaderService.showLoader();
    this.httpClientService.getQuestionnaireParticipante(token, idParticipante)
      .then(response => {
        if (response.status !== EnumStatusResponse.Success || !response.body) {
          return;
        }
        const questionarioRespondido = response.body as QuestionarioRespondido[];
        this.questionarioRespondido = questionarioRespondido;

        // Menor dtInclusao entre as respostas (datas ISO comparam lexicograficamente).
        const datasInclusao = questionarioRespondido
          .map(resposta => resposta.dtInclusao)
          .filter(Boolean);

        this.dataFinalizacao = datasInclusao.length
          ? datasInclusao.reduce((menor, atual) => (atual < menor ? atual : menor))
          : undefined;
      })
      .catch(error => {
        console.error(
          'Erro ao carregar os questionários do participante:',
          error
        );
        this.toastService.show('Não foi possível carregar os questionários do participante.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }

  private getQuestionnaire(token: string, idQuestionario: number): void {
    this.loaderService.showLoader();
    this.httpClientService.getQuestionnaire(token, idQuestionario)
      .then(response => {
        
        if (response.status === EnumStatusResponse.Success) {
          const body: Questionario[] = response.body as Questionario[];

          //ordenando as perguntas e alternativas caso não venham ordenadas do backend
          this.questionnaire = body.map((question: Questionario, questionIndex: number) => ({
            ...question,
            ordem: question.ordem ?? questionIndex + 1,
            alternativas: question.alternativas.map((option: Opcoes, optionIndex: number) => ({
              ...option,
              ordem: option.ordem ?? optionIndex + 1
            }))
          }));

          // Indexa por idPergunta para localizar a ordem sem percorrer a lista na renderização.
          this.perguntasPorId = new Map(
            this.questionnaire.map(pergunta => [pergunta.idPergunta, pergunta])
          );
        }
      })
      .catch(error => {
        console.error('Erro ao carregar o Questionnaire:', error);
        this.toastService.show('Não foi possível carregar o questionário. Tente novamente.', 'error');
      })
      .finally(() => this.loaderService.hideLoader());
  }
}
